import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  combineLatestWith,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';
import { GisDataModel, GisFieldsEnum } from '@bo-client/shared/models/gisData';
import { FormGroup, Validators } from '@angular/forms';
import { DevelopmentAreaInfo } from '@bo-client/shared/models/developmentAreas';
import { AllAreaEnum, AreaTypeEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { GisAddressService } from '@bo-client/shared/components/get-gis-data/gis-address.service';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { uri } from '@bo-client/shared/constants/uri';
import { GisDataEvent } from '@bo-client/shared/components/get-gis-data/get-gis-data.component';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

/**
 *
 */
@Component({
  selector: 'app-connection-object-address',
  templateUrl: './connection-object-address.component.html',
  styleUrls: ['./connection-object-address.component.scss']
})
export class ConnectionObjectAddressComponent implements OnInit, OnDestroy {
  @ViewChild('streetNoTrigger2', { read: MatAutocompleteTrigger })
  streetNoAutoTrigger: MatAutocompleteTrigger;
  @Input() askForDevelopmentArea = true;
  @Input() areaType: AreaTypeEnum;
  @Input() connectionObjectForm: FormGroup;
  @Input() disable = false;
  @Input() isNewDevelopmentArea = false;
  @Output() gisData: EventEmitter<GisDataEvent & { availableAreas: AllAreaEnum[] }> = new EventEmitter(); // Todo: Implement available areas -> addressSenseService.isAreaAvailable in forEach loop

  public isNotFound = false;
  public areaAvailable = true;
  private compUnsubs$ = new Subject<void>();
  private unsubChangeValues$ = new Subject<void>();

  public developmentAreasByZipCode$: Observable<DevelopmentAreaInfo[]>;
  _developmentAreaStreetName$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  developmentAreaStreetName$: Observable<string> = this._developmentAreaStreetName$.asObservable();

  public cityOptions: GisDataModel[] = null;
  public streetOptions: GisDataModel[] = null;
  public streetNoOptions: GisDataModel[] = null;
  public disableCorridorCheck = false;
  public isCompleted = false;
  public isSearching = false;
  public isNoHouseNumber = false;

  public fakeIdStreetNumberNotFound = '99999ABC';
  public gisFieldsEnum = GisFieldsEnum;
  public selectedGisData: GisDataModel;

  get streetNoSelected(): boolean {
    return !!this.connectionObjectForm.get('gisId')?.value;
  }

  get streetSelected(): boolean {
    return !!this.connectionObjectForm.get('street')?.value;
  }

  public showSelectMessage = false;

  get zipCodeCityReadable() {
    return (
      this.connectionObjectForm.get('zipCode').value + ' / ' + this.connectionObjectForm.get('city').value
    );
  }

  constructor(
    private httpHapService: HttpHapService,
    private gisAddressService: GisAddressService, // TODO cleanup => This makes no sense
    private getGisDataInterfaceService: GisAddressService
  ) {}

  ngOnInit(): void {
    this.isNewDevelopmentArea = !!this.connectionObjectForm.get('developmentAreaName')?.value;

    const { zipCodeCity, zipCode, city } = this.connectionObjectForm.controls;
    if (!zipCodeCity?.value && (zipCode?.value || city?.value)) {
      zipCodeCity.patchValue(zipCode.value + ' / ' + city.value);
    }
    this.developmentAreasByZipCode$ = this.httpHapService
      .getTyped<DevelopmentAreaInfo[]>(uri.getActiveDevelopmentAreas)
      .pipe(
        combineLatestWith(this.connectionObjectForm.get<string>('zipCode').valueChanges),
        map(([_, zipCode]) => _.filter((x) => x.zipCode === this.selectedGisData?.zipCode))
      );

    this.initFields();
    this.searchCityZipCode();
    this.searchStreet();
    this.searchStreetNo();
    this.searchNewDevelopmentArea();
    this.givedCorridorNumber();
  }

  closedStreetNoSelectWithoutSelection() {
    this.showSelectMessage = !this.streetNoSelected;
    if (!this.streetNoSelected) {
      this.connectionObjectForm.get('streetNo').setValidators([Validators.required]);
      this.connectionObjectForm.get('streetNo').patchValue('');
      this.connectionObjectForm.get('streetNoAddition').patchValue('');
      this.connectionObjectForm.get('gisId').patchValue('');
      this.connectionObjectForm.get('streetNo').markAsTouched();
    }
  }

  searchNewDevelopmentArea() {
    this.newDevelopmentAreaField.valueChanges
      .pipe(
        takeUntil(this.compUnsubs$),
        debounceTime(400),
        filter((val) => val != null && val !== ''),
        filter((val) => val.toString().length > 0),
        tap(() => (this.isSearching = true)),
        distinctUntilChanged(),
        switchMap((val) => this.getGisDataInterfaceService.getCitySuggestions(val)),
        tap(() => (this.isSearching = false))
      )
      .subscribe();
  }

  searchCityZipCode() {
    this.zipCodeCityField.valueChanges
      .pipe(
        takeUntil(this.compUnsubs$),
        debounceTime(400),
        filter((val) => val != null && val !== ''),
        filter((val) => val.toString().length > 0),
        tap(() => (this.isSearching = true)),
        distinctUntilChanged(),
        switchMap((val) => this.getGisDataInterfaceService.getCitySuggestions(val)),
        tap(() => (this.isSearching = false)),
        map((x) => (this.cityOptions = x))
      )
      .subscribe();
  }

  searchStreet() {
    this.streetField.valueChanges
      .pipe(
        takeUntil(this.compUnsubs$),
        debounceTime(400),
        filter((val) => val != null),
        filter((val) => val.toString().length > 0),
        tap(() => (this.isSearching = true)),
        distinctUntilChanged(),
        switchMap((val) => this.getGisDataInterfaceService.getStreetSuggestions(this.selectedGisData, val)),
        tap(() => (this.isSearching = false)),
        map((x) => (this.streetOptions = x))
      )
      .subscribe();
  }

  searchStreetNo() {
    this.isNotFound = false;
    this.streetNoField.valueChanges
      .pipe(
        takeUntil(this.compUnsubs$),
        debounceTime(400),
        filter((val) => val != null),
        filter((val) => val.toString().length > 0),
        tap(() => {
          this.isNotFound = false;
          this.isSearching = true;
          this.showSelectMessage = false;
        }),
        distinctUntilChanged(),
        switchMap((val) => this.getGisDataInterfaceService.getStreetNoSuggestions(this.selectedGisData, val)),
        tap(() => (this.isSearching = false)),
        map((res) => {
          this.streetNoOptions = res;
          if (this.streetNoOptions.length === 0) {
            this.isNotFound = true;
            this.streetNoOptions.push({
              id: this.fakeIdStreetNumberNotFound,
              houseNo: this.connectionObjectForm.get('streetNo').value
            });
          }
          this.streetNoAutoTrigger?.openPanel();
        })
      )
      .subscribe();
  }

  givedCorridorNumber() {
    this.isNotFound = false;
    this.corridorNumberField.valueChanges
      .pipe(
        takeUntil(this.compUnsubs$),
        debounceTime(400),
        filter((val) => val != null),
        filter((val) => val.toString().length > 0),
        distinctUntilChanged()
      )
      .subscribe((res) => {
        this.selectedGisData.corridorNumber = res;
      });
  }

  corridorNumberSelected() {
    if (
      this.isNoHouseNumber &&
      this.selectedGisData.corridorNumber &&
      this.selectedGisData.corridorNumber !== ''
    ) {
      this.confirmCorridorNumber();
    }
  }

  initFields() {
    const { corridorNumber, streetNo } = this.connectionObjectForm.controls;
    if (this.isNewDevelopmentArea || (!!corridorNumber?.value && !streetNo?.value)) {
      streetNo?.clearValidators();
      corridorNumber?.setValidators([Validators.required]);
      corridorNumber?.markAsTouched();
      this.isNoHouseNumber = true;
      this.disableCorridorCheck = false;
    } else {
      corridorNumber?.clearValidators();
      streetNo?.setValidators([Validators.required]);
      streetNo?.markAsTouched();
      this.isNoHouseNumber = false;
      this.disableCorridorCheck = true;
    }
  }

  resetGisData() {
    this.selectedGisData = null;
    this.isCompleted = false;
    this.cityOptions = null;
    this.streetNoOptions = null;

    this.isNotFound = false;
    // unsubscribe form valueChanges
    this.unsubChangeValues$.next();
    this.unsubChangeValues$.complete();
    this.connectionObjectForm.reset();
    this.connectionObjectForm.get('zipCodeCity').patchValue('');
    this.connectionObjectForm.get('street').patchValue('');
    this.connectionObjectForm.get('orgUnitDesc').patchValue('');
    this.connectionObjectForm.get('gisId').patchValue(null);

    this.resetHouseNoFormState();

    this.connectionObjectForm.get('street').disable();
    this.connectionObjectForm.get('streetNo').disable();

    this.connectionObjectForm.get('developmentAreaName').patchValue('');
    this.connectionObjectForm.get('developmentAreaName').disable();

    this.connectionObjectForm.get('corridorNumber').patchValue('');
    this.connectionObjectForm.get('corridorNumber').disable();
    this.disableCorridorCheck = true;
    this.isNoHouseNumber = false;

    this.searchStreet();
    this.searchStreetNo();
    this.gisData.emit({
      gisData: this.selectedGisData,
      objectNotFound: this.isNotFound,
      areaAvailable: this.areaAvailable,
      availableAreas: []
    });
  }

  resetHouseNoFormState() {
    this.showSelectMessage = false;
    if (this.isNewDevelopmentArea || this.isNoHouseNumber) {
      this.connectionObjectForm.get('corridorNumber').setValidators([Validators.required]);
      this.connectionObjectForm.get('corridorNumber').patchValue('');
      this.connectionObjectForm.get('corridorNumber').markAsTouched();
      this.connectionObjectForm.get('corridorNumber').enable();
      this.connectionObjectForm.get('streetNo').clearValidators();
      this.connectionObjectForm.get('streetNo').patchValue(null);
      this.connectionObjectForm.get('streetNo').markAsUntouched();
      this.connectionObjectForm.get('streetNoAddition').patchValue(null);
      this.connectionObjectForm.get('streetNoAddition').markAsUntouched();
      this.connectionObjectForm.get('gisId').patchValue(null);
      this.connectionObjectForm.get('gisId').markAsUntouched();
      this.givedCorridorNumber();
    } else {
      this.resetStreetNo();
      this.connectionObjectForm.get('corridorNumber').clearValidators();
      this.connectionObjectForm.get('corridorNumber').patchValue(null);
      this.connectionObjectForm.get('corridorNumber').markAsUntouched();
      this.connectionObjectForm.get('corridorNumber').enable();
      this.connectionObjectForm.get('streetNo').setValidators([Validators.required]);
      this.connectionObjectForm.get('streetNo').patchValue('');
      this.connectionObjectForm.get('streetNo').markAsTouched();
    }
  }

  selectedOption(option: GisDataModel | DevelopmentAreaInfo, field: GisFieldsEnum) {
    this.resetChangeValues();

    if (!this.selectedGisData) {
      this.selectedGisData = new GisDataModel();
    }
    // load selectedGisDataFields
    this.selectedGisData = this.gisAddressService.loadSelectedGisData(field, option, this.selectedGisData);
    this._developmentAreaStreetName$.next(null);

    this.connectionObjectForm.get('orgUnitDesc').patchValue(this.selectedGisData.orgEinheit);

    let availableAreas: AllAreaEnum[] = [];

    // Set Form fields
    switch (field) {
      case GisFieldsEnum.zipCode:
        // this.gisDataForm.
        option = option as GisDataModel;
        this.connectionObjectForm.get('street').patchValue('');
        this.connectionObjectForm.get('city').patchValue(option.city);
        this.connectionObjectForm.get('zipCode').patchValue(option.zipCode);
        this.resetStreetNo();
        this.streetOptions = null;
        this.streetNoOptions = null;
        this.connectionObjectForm.get('street').enable();
        this.connectionObjectForm.get('streetNo').disable();
        this.connectionObjectForm.get('developmentAreaName').enable();
        this.connectionObjectForm.get('corridorNumber').disable();
        break;
      case GisFieldsEnum.street:
        option = option as GisDataModel;
        this.streetNoOptions = null;
        this.resetStreetNo();
        // Todo.. make this reactive..
        this.connectionObjectForm.get('streetNo').enable();
        this.disableCorridorCheck = false;
        break;
      case GisFieldsEnum.streetNo:
        option = option as GisDataModel;
        // tslint:disable-next-line:triple-equals
        if (option.id != this.fakeIdStreetNumberNotFound) {
          this.areaAvailable = this.getGisDataInterfaceService.isAvailableArea(this.areaType, [], option);
          availableAreas = Object.values(AllAreaEnum).filter((value) =>
            this.gisAddressService.isAvailableArea(
              value.toUpperCase() as AreaTypeEnum,
              [],
              this.selectedGisData
            )
          );
        }
        // if streetNo not found => rewrite houseNo with the fieldvalue
        if (this.isNotFound) {
          this.selectedGisData.houseNo = this.connectionObjectForm.get('streetNo').value;
        } else {
        }
        this.connectionObjectForm.get('');
        this.connectionObjectForm.get('streetNoAddition').patchValue(this.selectedGisData.houseNoAddition);
        this.connectionObjectForm.get('streetNo').patchValue(this.selectedGisData.houseNo);
        this.connectionObjectForm.get('gisId').patchValue(option.houseNo);
        this.connectionObjectForm
          .get('gisHouseConnectionPoint')
          .patchValue(
            option.gridConnectionPoints?.find((gP) => gP.area.toLowerCase() === this.areaType.toLowerCase())
              ?.value ?? null
          );

        break;
      case GisFieldsEnum.developmentArea:
        option = option as DevelopmentAreaInfo;
        this._developmentAreaStreetName$.next(option.defaultStreetName);
        // Set Form fields
        this.connectionObjectForm.get('corridorNumber').patchValue('');
        this.connectionObjectForm.get('corridorNumber').enable();
        break;
    }

    this.selectedGisData.isNewDevelopmentArea = this.isNewDevelopmentArea;
    this.gisData.emit({
      gisData: this.selectedGisData,
      objectNotFound: this.isNotFound,
      areaAvailable: this.areaAvailable,
      availableAreas
    });
  }

  private resetConnectionObject() {
    this.resetGisData();
  }

  private resetStreet() {
    this.connectionObjectForm.get('street').patchValue('');
    this.connectionObjectForm.get('street').enable();
  }

  private resetStreetNo() {
    this.connectionObjectForm.get('streetNo').patchValue('');
    this.connectionObjectForm.get('streetNoAddition').patchValue('');
  }

  saveData() {
    this.connectionObjectForm.get('streetNo').clearValidators();
    this.connectionObjectForm.get('streetNo').markAsTouched();
    this.isCompleted = true;
  }

  confirmCorridorNumber() {
    this.connectionObjectForm.get('gisId').patchValue(this.selectedGisData.uniqueCoId);
    this.areaAvailable = true;
    this.isCompleted = true;
  }

  get zipCodeCityField() {
    return this.connectionObjectForm.get('zipCodeCity');
  }

  get streetField() {
    return this.connectionObjectForm.get('street');
  }

  get streetNoField() {
    return this.connectionObjectForm.get('streetNo');
  }

  get streetNoAdditionField() {
    return this.connectionObjectForm.get('streetNoAddition');
  }

  get newDevelopmentAreaField() {
    return this.connectionObjectForm.get('developmentAreaName');
  }

  get corridorNumberField() {
    return this.connectionObjectForm.get('corridorNumber');
  }

  resetChangeValues() {
    this.compUnsubs$.next();
    setTimeout(() => {
      this.searchCityZipCode();
      this.searchNewDevelopmentArea();
      this.searchStreet();
      this.searchStreetNo();
      this.givedCorridorNumber();
    }, 400);
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }
}
